// @flow
/* eslint-disable css-modules/no-undef-class */

import * as React from 'react';
import { OverlayPortal } from '@ta/lithium.portals';
import classNames from '@ta/common.classnames';
import styles from './Overlay.css';

type OverlayPosition = 'absolute' | 'fixed';

type CSSValue = string | number;

/** Position of overlay */
export type Position = {|
  /** The position of the overlay, either 'absolute' or 'fixed'. Defaults to absolute. */
  position?: OverlayPosition,

  /**
   * The top offset for the overlay. Should be in the same format as the 'top' CSS property (i.e. '10px', '5%', etc.)
   */
  top?: CSSValue,

  /**
   * The bottom offset for the overlay. Should be in the same format as the 'bottom' CSS property (i.e. '10px', '5%',
   * etc.)
   */
  bottom?: CSSValue,

  /**
   * The right offset for the overlay. Should be in the same format as the 'right' CSS property (i.e. '10px', '5%',
   * etc.)
   */
  right?: CSSValue,

  /**
   * The left offset for the overlay. Should be in the same format as the 'left' CSS property (i.e. '10px', '5%', etc.)
   */
  left?: CSSValue,

  /** */
  transform?: string,

  /**
   * Raw max-height value to set on overlay style. Examples include '400px', '100%', etc.
   */
  maxHeight?: CSSValue,

  /**
   * Raw height value to set on overlay style. Examples include '400px', '100%', etc.
   */
  height?: CSSValue,

  /**
   * Raw width value to set on overlay style. Examples include '400px', '100%', etc.
   */
  width?: CSSValue,

  /**
   * Raw transform-origin CSS value to set the origin of the overlay's transform.
   */
  transformOrigin?: string,

  /** @Deprecated - Don't manipulate the z-indexes!
   * Raw z-index to use on the overlay. defaults to var(--overlays)
   */
  zIndex?: string,
|};

/**
 * Defines the animation's transition timing and function for a particular CSS property.
 * Properties are enumerated to maintain awareness of what we're supporting, and if we switched to a different
 * property (e.g. translate3d instead of top) in the future we can just add a mapping.
 */
type TransitionProperties = {|
  /** Properties supported for transitions */
  property: 'opacity' | 'top' | 'left' | 'right' | 'bottom' | 'transform' | 'max-height',

  /** Number of milliseconds to animate for */
  timing: number,

  /** Optional. Transition timing function (https://developer.mozilla.org/en-US/docs/Web/CSS/transition-timing-function) */
  timingFunction?: string,

  /** Optional. Time to delay a transition in milliseconds. */
  delay?: number,
|};

type Props = {|
  /** The positioning info for the modal. See the 'Position' type. */
  position: Position,

  /** Optional perspective style for the re-parented container */
  perspective?: string,

  /** Optional opacity for the overlay. Useful for transitions. */
  opacity?: string,

  /** The child react nodes */
  children: React.Node,

  /** Optional transition properties */
  transitionProperties?: TransitionProperties[],

  /** Autofocus the modal upon opening */
  autoFocus?: boolean,

  /** @Deprecated - Don't manipulate the z-indexes! These values don't mean anything. */
  overlayType?: 'modal' | 'background' | 'tooltip' | 'popover',
|};

/* istanbul ignore next */
const isFocusable = (element: any) => {
  if (!element || element.getAttribute('data-skip-focus') || element.disabled) {
    return false;
  }
  if (
    element.tabIndex > 0 ||
    (element.tabIndex === 0 && element.getAttribute('tabIndex') !== null)
  ) {
    return true;
  }

  switch (element.nodeName) {
    case 'A':
      return element.href && element.rel !== 'ignore';
    case 'INPUT':
      return element.type !== 'hidden' && element.type !== 'file';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return false;
  }
};

/* istanbul ignore next */
const attemptFocus = (element: HTMLElement) => {
  if (!isFocusable(element)) {
    return false;
  }

  element.focus();

  // See if the element is now focused.
  // eslint-disable-next-line no-restricted-globals
  return document.activeElement === element;
};

/* istanbul ignore next */
const focusFirstDescendant = (element: HTMLElement) => {
  if (!element) {
    return false;
  }
  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    if (attemptFocus(child) || focusFirstDescendant(child)) {
      return true;
    }
  }
  return false;
};

/* istanbul ignore next */
const focusLastDescendant = (element: HTMLElement) => {
  if (!element) {
    return false;
  }
  for (let i = element.children.length - 1; i >= 0; i--) {
    const child = element.children[i];
    if (attemptFocus(child) || focusLastDescendant(child)) {
      return true;
    }
  }
  return false;
};

/** Base overlay */
class Overlay extends React.Component<Props> {
  static defaultProps = {
    perspective: undefined,
    overlayType: 'modal',
  };

  componentDidMount(): void {
    if (this.props.overlayType === 'modal') {
      this.addFocusTrapListeners();
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.overlayType !== 'modal' && this.props.overlayType === 'modal') {
      this.addFocusTrapListeners();
    } else if (prevProps.overlayType === 'modal' && this.props.overlayType !== 'modal') {
      this.removeFocusTrapListeners();
    }
  }

  // Here code coverage was disabled since because we can directly call BaseOverlay unmount in test cases
  /* istanbul ignore next */
  componentWillUnmount() {
    this.removeFocusTrapListeners();
  }

  /**
   * Any styles that can be animated should be defined in this function so that we set the the styles in both the constructor and in render().
   * It's necessary to set in the constructor so that the overlay DOM node has an initial value for the transition.
   */
  getStyles() {
    const style = {};
    style.perspective = this.props.perspective;
    if (this.props.transitionProperties) {
      const transition = [];
      this.props.transitionProperties.forEach(trans => {
        // Timing function is optional. If not specified, browser will use default (ease).
        const transitionFunction = trans.timingFunction ? ` ${trans.timingFunction}` : '';
        const delay = trans.delay ? ` ${trans.delay}ms` : '';
        transition.push(`${trans.property} ${trans.timing}ms${transitionFunction}${delay}`);
      });

      if (transition.length > 0) {
        style.transition = transition.join(', ');
      }
    }

    // Collect transforms and apply at the end instead of adding onto the style since the component will build up extra
    // transforms if it re-renders. E.g., modal rerenders when switching from desktop to another breakpoint.
    const transforms = [];

    // apply the position to the overlay node
    style.position = this.props.position.position || 'absolute';

    // for each axis apply any provided offset
    ['top', 'left', 'bottom', 'right'].forEach((axis: string) => {
      const offset: string = this.props.position[axis];
      if (offset) {
        style[axis] = offset;
      } else {
        style[axis] = 'auto';
      }
    });

    if (this.props.position.zIndex) {
      style.zIndex = this.props.position.zIndex;
    }

    // if we have a transformOrigin prop then apply to the overlay node.
    if (this.props.position.transformOrigin) {
      style.transformOrigin = this.props.position.transformOrigin;
    }

    // if we have a transform prop then apply to the overlay node.
    if (this.props.position.transform) {
      transforms.push(this.props.position.transform);
    }

    style.transform = transforms.join(' ');

    // if we have a maxHeight prop then apply to the overlay node.
    if (this.props.position.maxHeight) {
      style.maxHeight = this.props.position.maxHeight;
      style.overflowY = 'hidden';
    }

    // if we have a height prop then apply to the overlay node.
    if (this.props.position.height) {
      style.height = this.props.position.height;
    }

    // if we have a width prop then apply to the overlay node.
    if (this.props.position.width) {
      style.width = this.props.position.width;
    }

    if (typeof this.props.opacity !== 'undefined') {
      style.opacity = this.props.opacity;
    }
    return style;
  }

  addFocusTrapListeners() {
    /* istanbul ignore next */
    if (this.focusTrap1.current) {
      this.focusTrap1.current.focus();
    }
    if (this.focusTrap1.current) {
      this.focusTrap1.current.addEventListener('focus', this.focusLastDescendant);
    }
    if (this.focusTrap2.current) {
      this.focusTrap2.current.addEventListener('focus', this.focusFirstDescendant);
    }
  }

  removeFocusTrapListeners() {
    if (this.focusTrap1.current) {
      this.focusTrap1.current.removeEventListener('focus', this.focusLastDescendant);
    }
    if (this.focusTrap2.current) {
      this.focusTrap2.current.removeEventListener('focus', this.focusFirstDescendant);
    }
  }

  focusFirstDescendant = () => {
    if (this.overlayNode.current) {
      focusFirstDescendant(this.overlayNode.current);
    }
  };

  focusLastDescendant = () => {
    if (this.overlayNode.current) {
      focusLastDescendant(this.overlayNode.current);
    }
  };

  overlayNode = React.createRef<HTMLDivElement>();

  focusTrap1 = React.createRef<HTMLDivElement>();
  focusTrap2 = React.createRef<HTMLDivElement>();

  render() {
    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    const className = classNames(styles.overlay, {
      [styles.background]: this.props.overlayType === 'background',
      [styles.tooltip]: this.props.overlayType === 'tooltip',
      [styles.popover]: this.props.overlayType === 'popover',
    });

    const style = this.getStyles();
    const overlay = this.props.autoFocus ? (
      <>
        <div tabIndex={0} ref={this.focusTrap1} data-skip-focus />
        {this.props.children}
        <div tabIndex={0} ref={this.focusTrap2} data-skip-focus />
      </>
    ) : (
      this.props.children
    );
    return (
      <OverlayPortal style={style} className={className} ref={this.overlayNode}>
        {overlay}
      </OverlayPortal>
    );
  }
}

export default Overlay;
